/**
 * CRUD handlers (create, update, delete, reset-password) for User Management.
 *
 * Extracted from useUserHandlers (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { createUser, updateUser, deleteUser, resetUserPassword } from '../../services/admin'
import type { User, CreateUserRequest, UpdateUserRequest } from '../../types'
import type { CreateUserForm } from './types'

interface CrudDeps {
  selectedUser: User | null
  formData: CreateUserForm
  setFormLoading: (v: boolean) => void
  setFormError: (msg: string | null) => void
  setError: (msg: string | null) => void
  setShowCreateModal: (v: boolean) => void
  setShowEditModal: (v: boolean) => void
  setShowDeleteModal: (v: boolean) => void
  setShowResetPasswordModal: (v: boolean) => void
  setIssuedPassword: (v: string | null) => void
  setSelectedUser: (user: User | null) => void
  resetForm: () => void
  loadData: () => Promise<void>
}

interface CrudHandlers {
  handleCreateUser: (e: React.FormEvent) => Promise<void>
  handleUpdateUser: (e: React.FormEvent) => Promise<void>
  handleDeleteUser: () => Promise<void>
  handleResetPassword: () => Promise<void>
}

function buildCreateRequest(data: CreateUserForm): CreateUserRequest {
  const req: CreateUserRequest = {
    email: data.email,
    username: data.username,
    role: data.role,
  }
  if (data.displayName) req.displayName = data.displayName
  if (data.organisationId) req.organisationId = data.organisationId
  return req
}

function buildUpdateRequest(data: CreateUserForm): UpdateUserRequest {
  const req: UpdateUserRequest = { role: data.role }
  if (data.displayName) req.displayName = data.displayName
  if (data.organisationId) req.organisationId = data.organisationId
  return req
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function useUserCrudHandlers(deps: CrudDeps): CrudHandlers {
  const { t } = useTranslation('admin')

  const handleCreateUser = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    deps.setFormLoading(true)
    deps.setFormError(null)
    try {
      const created = await createUser(buildCreateRequest(deps.formData))
      // Shown once — the server keeps only the hash, so discarding it here strands the account.
      deps.setIssuedPassword(created.issuedPassword)
      deps.setShowCreateModal(false)
      deps.resetForm()
      void deps.loadData()
    } catch (err: unknown) {
      deps.setFormError(errorMessage(err, t('users.errors.createFailed')))
    } finally {
      deps.setFormLoading(false)
    }
  }

  const handleUpdateUser = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!deps.selectedUser) return
    deps.setFormLoading(true)
    deps.setFormError(null)
    try {
      await updateUser(deps.selectedUser.id, buildUpdateRequest(deps.formData))
      deps.setShowEditModal(false)
      deps.setSelectedUser(null)
      deps.resetForm()
      void deps.loadData()
    } catch (err: unknown) {
      deps.setFormError(errorMessage(err, t('users.errors.updateFailed')))
    } finally {
      deps.setFormLoading(false)
    }
  }

  const handleDeleteUser = async (): Promise<void> => {
    if (!deps.selectedUser) return
    deps.setFormLoading(true)
    deps.setFormError(null)
    try {
      await deleteUser(deps.selectedUser.id)
      deps.setShowDeleteModal(false)
      deps.setSelectedUser(null)
      void deps.loadData()
    } catch (err: unknown) {
      deps.setFormError(errorMessage(err, t('users.errors.deleteFailed')))
    } finally {
      deps.setFormLoading(false)
    }
  }

  const handleResetPassword = async (): Promise<void> => {
    if (!deps.selectedUser) return
    deps.setFormLoading(true)
    deps.setFormError(null)
    try {
      const { password } = await resetUserPassword(deps.selectedUser.id)
      deps.setShowResetPasswordModal(false)
      deps.setSelectedUser(null)
      deps.setError(null)
      // Surfaced by IssuedPasswordModal. This is the only time it can be seen — the server keeps
      // only the Argon2 hash.
      deps.setIssuedPassword(password)
    } catch (err: unknown) {
      deps.setFormError(errorMessage(err, t('users.errors.resetPasswordFailed')))
    } finally {
      deps.setFormLoading(false)
    }
  }

  return { handleCreateUser, handleUpdateUser, handleDeleteUser, handleResetPassword }
}
